import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SuccessIcon, GooglePlayIcon, AppStoreIcon } from '../../assets/icons';
import { getBrandSiteUrl } from '../../utils/getDomainBrand';
import QRCodeService from '../../services/qrCode.service';
import { RoleConfig } from './roleConfig';

interface LocationState {
  username: string;
  name?: string;
  password: string;
  phoneNumber?: string;
  backendUrl: string;
}

interface RoleSuccessPageProps {
  config: RoleConfig;
  styles: Record<string, string>;
  // Required when config.successPageMode === 'static-qr'
  staticQrImage?: string;
}

const RoleSuccessPage: React.FC<RoleSuccessPageProps> = ({ config, styles, staticQrImage }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | undefined;
  const [dynamicQr, setDynamicQr] = useState<string>('');

  useEffect(() => {
    if (config.successPageMode !== 'dynamic-qr') return;
    QRCodeService.getInstance()
      .generateQRCode(`${getBrandSiteUrl()}/`, true)
      .then(setDynamicQr)
      .catch(() => setDynamicQr(''));
  }, [config.successPageMode]);

  if (!state) {
    return (
      <div className={styles.container}>
        <h2 className={styles.title}>{config.emptyStateMessage}</h2>
        <button className={styles.createBtn} onClick={() => navigate(config.routeBase)}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const qrSrc = config.successPageMode === 'dynamic-qr' ? dynamicQr : staticQrImage;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <SuccessIcon width={56} height={56} />
        <h2 className={styles.title}>{config.successHeading}</h2>
        <div className={styles.infoCard}>
          <div className={styles.infoContent}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Username:</span>{' '}
              <span className={styles.value}>{state.username}</span>
            </div>
            {config.successShowsName && state.name && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Name:</span>{' '}
                <span className={styles.value}>{state.name}</span>
              </div>
            )}
            <div className={styles.infoRow}>
              <span className={styles.label}>Password:</span>{' '}
              <span className={styles.value}>{state.password}</span>
            </div>
            {config.successShowsPhone && state.phoneNumber && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Phone:</span>{' '}
                <span className={styles.value}>{state.phoneNumber}</span>
              </div>
            )}
            <div className={styles.infoRow}>
              <span className={styles.label}>Backend URL:</span>{' '}
              <span className={styles.backendUrl}>{state.backendUrl}</span>
            </div>
            <div className={styles.storeIcons}>
              <GooglePlayIcon className={styles.storeIcon} />
              <AppStoreIcon className={styles.storeIcon} />
            </div>
          </div>
          <div className={styles.qrWrapper}>
            {qrSrc && (
              <img src={qrSrc} alt={`${config.label} QR Code`} className={styles.qrCode} />
            )}
            <div className={styles.qrCaption}>Scan to access URL</div>
          </div>
        </div>
        <button
          className={`${styles.createBtn} ${styles.backButton}`}
          onClick={() => navigate(config.routeBase)}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default RoleSuccessPage;
